import Typography from '@mui/material/Typography';
import { useStore } from '@tanstack/react-form';

import Box from '~/common/components/Box';
import DatePicker from '~/common/components/DatePicker';
import Flex from '~/common/components/Flex';
import FormControl from '~/common/components/Forms/FormControl';
import FormErrorMessage from '~/common/components/Forms/FormErrorMessage';
import CurrencyInput from '~/common/components/Inputs/CurrencyInput';
import Input from '~/common/components/Inputs/Input';
import InputGroup from '~/common/components/Inputs/InputGroup';
import Select from '~/common/components/Selects/Select';
import Stack from '~/common/components/Stacks/Stack';
import Divider from '~/common/mui/Divider';

type TaxProrationsProps = {
  form: any;
};

type TaxProrationField = {
  companyName?: string;
  taxesPaidFlag?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  amount?: number | null;
  sellerPaidAmount?: number | null;
  originalHudNumber?: string | number | null;
};

type TaxProrationsSelectorState = {
  values: {
    prorationsData?: TaxProrationField[];
  };
};

const CHARGE_OR_CREDIT_REQUIRED_MESSAGE = 'Charge or Credit is required';
const PRORATION_AMOUNT_REQUIRED_MESSAGE = 'Proration amount is required';

const hasProrationAmount = (amount?: number | string | null) => {
  return !!Number(amount);
};

const createChargeOrCreditValidator =
  (form: any, idx: number) =>
  ({ value }: { value?: string | null }) => {
    const amount = form.getFieldValue(`prorationsData[${idx}].amount`);

    return hasProrationAmount(amount) && !value
      ? CHARGE_OR_CREDIT_REQUIRED_MESSAGE
      : undefined;
  };

const createProrationAmountValidator =
  (form: any, idx: number) =>
  ({ value }: { value?: number | string | null }) => {
    const taxesPaidFlag = form.getFieldValue(
      `prorationsData[${idx}].taxesPaidFlag`,
    );

    return taxesPaidFlag && !hasProrationAmount(value)
      ? PRORATION_AMOUNT_REQUIRED_MESSAGE
      : undefined;
  };

const TaxProrations = ({ form }: TaxProrationsProps) => {
  const fields =
    useStore(
      form.store,
      (state) => (state as TaxProrationsSelectorState).values.prorationsData,
    ) ?? [];

  return (
    <Stack
      sx={{
        mb: '2rem',
      }}
    >
      {fields.map((field, idx) => (
        <Box
          key={
            field?.originalHudNumber ??
            `${field?.companyName ?? 'proration'}-${field?.fromDate ?? ''}-${field?.toDate ?? ''}`
          }
          sx={{ width: '100%', p: '0' }}
        >
          <Flex direction="row" alignItems="flex-end" width="100%" gap="1.5rem">
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                component="label"
                variant="h3"
                htmlFor={`taxProration.${idx}.companyName`}
              >
                Tax Entity
              </Typography>
              <form.Field name={`prorationsData[${idx}].companyName`}>
                {(fieldApi) => (
                  <Input
                    type="text"
                    id={`taxProration.${idx}.companyName`}
                    width="100%"
                    name={fieldApi.name}
                    value={fieldApi.state.value ?? ''}
                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                    onBlur={fieldApi.handleBlur}
                  />
                )}
              </form.Field>
            </Box>
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                component="label"
                variant="h3"
                htmlFor={`taxProration.${idx}.taxesPaidFlag`}
              >
                Charge or Credit
              </Typography>
              <InputGroup style={{ width: '100%' }}>
                <form.Field
                  name={`prorationsData[${idx}].taxesPaidFlag`}
                  validators={{
                    onSubmit: createChargeOrCreditValidator(form, idx),
                    onChangeListenTo: [`prorationsData[${idx}].amount`],
                    onChange: createChargeOrCreditValidator(form, idx),
                  }}
                >
                  {(fieldApi) => {
                    const invalid = fieldApi.state.meta.errors.length > 0;
                    return (
                      <FormControl
                        isInvalid={invalid}
                        sx={{ width: '100%', position: 'relative' }}
                      >
                        <Select
                          id={`taxProration.${idx}.taxesPaidFlag`}
                          name={fieldApi.name}
                          value={fieldApi.state.value ?? ''}
                          isInvalid={invalid}
                          onChange={(e) => {
                            fieldApi.handleChange(e.target.value || null);
                            const newValue = e.target.value;
                            const amount = form.getFieldValue(
                              `prorationsData[${idx}].amount`,
                            );

                            if (newValue === 'Charge') {
                              form.setFieldValue(
                                `prorationsData[${idx}].sellerPaidAmount`,
                                0,
                              );
                            } else if (newValue === 'Credit') {
                              form.setFieldValue(
                                `prorationsData[${idx}].sellerPaidAmount`,
                                amount,
                              );
                            }
                          }}
                          sx={{
                            width: '100%',
                          }}
                        >
                          <option key="select-an-option" value={''}>
                            Select an Option
                          </option>
                          <option key="charge" value="Charge">
                            Charge
                          </option>
                          <option key="credit" value="Credit">
                            Credit
                          </option>
                        </Select>
                        <FormErrorMessage
                          sx={{ position: 'absolute', top: '100%', mt: '2px' }}
                        >
                          {fieldApi.state.meta.errors[0] as string}
                        </FormErrorMessage>
                      </FormControl>
                    );
                  }}
                </form.Field>
              </InputGroup>
            </Box>
          </Flex>
          <Flex
            direction="row"
            alignItems="flex-end"
            width="100%"
            gap="1.5rem"
            mt="2rem"
          >
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <form.Field name={`prorationsData[${idx}].fromDate`}>
                {(fieldApi) => {
                  const invalid = fieldApi.state.meta.errors.length > 0;
                  return (
                    <DatePicker
                      label="From"
                      width="100%"
                      name={`taxProration.${idx}.fromDate`}
                      value={fieldApi.state.value || null}
                      onChange={fieldApi.handleChange}
                      error={invalid}
                    />
                  );
                }}
              </form.Field>
            </Box>
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <form.Field name={`prorationsData[${idx}].toDate`}>
                {(fieldApi) => {
                  const invalid = fieldApi.state.meta.errors.length > 0;
                  return (
                    <DatePicker
                      label="To"
                      width="100%"
                      name={`taxProration.${idx}.toDate`}
                      value={fieldApi.state.value || null}
                      onChange={fieldApi.handleChange}
                      error={invalid}
                    />
                  );
                }}
              </form.Field>
            </Box>
          </Flex>
          <Flex
            direction="row"
            alignItems="flex-end"
            width="100%"
            gap="1.5rem"
            mt="2rem"
          >
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                component="label"
                variant="h3"
                htmlFor={`taxProration.${idx}.amount`}
              >
                Proration
              </Typography>
              <form.Field
                name={`prorationsData[${idx}].amount`}
                validators={{
                  onSubmit: createProrationAmountValidator(form, idx),
                  onChangeListenTo: [`prorationsData[${idx}].taxesPaidFlag`],
                  onChange: createProrationAmountValidator(form, idx),
                }}
              >
                {(fieldApi) => {
                  const invalid = fieldApi.state.meta.errors.length > 0;
                  return (
                    <FormControl
                      isInvalid={invalid}
                      sx={{ width: '100%', position: 'relative' }}
                    >
                      <CurrencyInput
                        width="100%"
                        name={fieldApi.name}
                        id={`taxProration.${idx}.amount`}
                        value={fieldApi.state.value}
                        isInvalid={invalid}
                        onChange={(newValue) => {
                          fieldApi.handleChange(newValue);
                          const taxesPaidFlag = (form.state.values
                            .prorationsData || [])[idx]?.taxesPaidFlag;
                          if (taxesPaidFlag === 'Credit') {
                            form.setFieldValue(
                              `prorationsData[${idx}].sellerPaidAmount`,
                              newValue,
                            );
                          }
                        }}
                        data-testid={`taxProration.${idx}.amount`}
                      />
                      <FormErrorMessage
                        sx={{ position: 'absolute', top: '100%', mt: '2px' }}
                      >
                        {fieldApi.state.meta.errors[0] as string}
                      </FormErrorMessage>
                    </FormControl>
                  );
                }}
              </form.Field>
            </Box>
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                component="label"
                variant="h3"
                htmlFor={`taxProration.${idx}.sellerPaidAmount`}
              >
                Seller Paid Amount
              </Typography>
              <form.Field name={`prorationsData[${idx}].sellerPaidAmount`}>
                {(fieldApi) => (
                  <CurrencyInput
                    isDisabled
                    width="100%"
                    name={fieldApi.name}
                    value={fieldApi.state.value}
                    data-testid={`taxProration.${idx}.sellerPaidAmount`}
                  />
                )}
              </form.Field>
            </Box>
          </Flex>
          {idx < fields.length - 1 && (
            <Divider sx={{ marginTop: 32, marginBottom: 14 }} />
          )}
        </Box>
      ))}
    </Stack>
  );
};

export default TaxProrations;
